import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import { StudentCard } from './Components/StudentCard';
import { LeaderboardTable } from './Components/LeaderBoardTable';
import { PerformanceModal } from './Components/PerformanceModal';
import { Tab, TabsContainer } from '../utils/SubjectTabs';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  usePagination,
  PaginationEllipsis,
} from '../components/ui/pagination';
import AllQs from '../../public/Icons/AllSubjectsB&W.svg';
import Q3Scroll from '../../public/Icons/Q3-Scroll.svg';
import { LeaderboardSkeleton } from './Components/Skeletons/LeaderBoardSkeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';

const formatSubjects = (subjects, allAvailableSubjects) => {
  if (!subjects || subjects.length === 0) {
    return {};
  }
  const formatted = {};
  subjects.forEach((studentSubject) => {
    const subjectDetail = (allAvailableSubjects || []).find((s) => s.subjectId?._id === studentSubject.subjectId._id);
    if (subjectDetail) {
      const key = subjectDetail.subjectId.shortName
        ? subjectDetail.subjectId.shortName.toLowerCase()
        : subjectDetail.subjectId.title.toLowerCase();
      formatted[key] = Number.isInteger(studentSubject.totalMarkScored)
        ? studentSubject.totalMarkScored
        : studentSubject.totalMarkScored?.toFixed(2);
    }
  });
  return formatted;
};

const transformLeaderboardData = (data, testDetails, currentUserId, allAvailableSubjects, isCombined) => {
  let totalMarks = testDetails?.totalMarks || 300;
  if (testDetails?.linkedTests?.length > 0) {
    totalMarks = testDetails?.linkedTests?.reduce((acc, curr) => acc + curr.totalMarks, totalMarks);
  }

  return data.map((student) => {
    const userId = student.userId || {};
    const userName = userId.name;
    const userAvatar = userId.profilePicture;
    return {
      rank: student.rank,
      name: userName,
      avatar: userAvatar,
      overallScore: student?.totalMarkScored ?? student?.totalMarks,
      accuracy: Number.parseFloat(student.accuracy).toFixed(2),
      maxScore: isCombined && totalMarks ? totalMarks : testDetails?.totalMarks || 300,
      ...formatSubjects(student.subjects, allAvailableSubjects),
      isCurrentUser: userId._id === currentUserId,
      raw: student,
    };
  });
};

const formatTestTitle = (title) => {
  if (!title) return '';
  let cleanedTitle = title.replace(/ – with Video Solutions/g, '').trim();
  const abbreviationRules = [
    {
      regex: /^Quizrr Advanced Full Test (\d+) \(Paper (\d+)\)$/i,
      format: (match) => {
        const testNumber = match[1];
        const paperNumber = match[2];
        return `QAFT ${testNumber} (Paper ${paperNumber})`;
      },
    },
    {
      regex: /^Quizrr Part Test \(QPT\) - (\d+)$/i,
      format: (match) => {
        const testNumber = match[1];
        return `QPT ${testNumber}`;
      },
    },
    {
      regex:
        /^Quizrr\s*(?:(?:Advanced Full Test|Part Test \(QPT\)|Sectional Test)?(?: - )?)?(\d+)?(?:\s*\(Paper (\d+)\))?$/i,
      format: (match) => {
        let abbreviation = 'QZ';
        const testNumber = match[1];
        const paperNumber = match[2];
        if (title.toLowerCase().includes('advanced full test')) {
          abbreviation = 'QAFT';
        } else if (title.toLowerCase().includes('part test (qpt)')) {
          abbreviation = 'QPT';
        }
        let parts = [abbreviation];
        if (testNumber) {
          parts.push(testNumber);
        }
        if (paperNumber) {
          parts.push(`(Paper ${paperNumber})`);
        }
        return parts.join(' ');
      },
    },
  ];
  for (const rule of abbreviationRules) {
    const match = cleanedTitle.match(rule.regex);
    if (match) {
      return rule.format(match);
    }
  }
  return cleanedTitle;
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);

  return matches;
}

export default function Leaderboard() {
  const [searchParams] = useSearchParams();
  const isMobileOrTablet = useMediaQuery('(max-width: 767px)');
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const { packId, testId } = useParams();
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem('userProfile'));
  const currentUserId = userProfile?._id;
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [overallTopStudents, setOverallTopStudents] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [testDetails, setTestDetails] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCombinedLocked, setIsCombinedLocked] = useState(false);
  const [linkedTests, setLinkedLists] = useState([]);
  const mainTestTitleRef = useRef('');

  const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') || testId);
  const [isSingleSubject, setIsSingleSubject] = useState(false);
  const [isSingleSectionMultipleSubjects, setIsSingleSectionMultipleSubjects] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const paginationRange = usePagination({ totalPages: totalPages, currentPage: currentPage });
  const [isStickyFooterCompare, setIsStickyFooterCompare] = useState(false);
  const [totalResults, settotalResults] = useState(0);
  const [showStickyFooter, setShowStickyFooter] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);

  const tabsInitializedRef = useRef(false);
  const stickyFooterRef = useRef(null);
  const isScrollingSyncRef = useRef(false);

  const tabsContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const hasTrackedRef = useRef(false);
  const leaderboardCacheRef = useRef({});
  const topStudentsCacheRef = useRef({});

  const handleMainTableScroll = (scrollLeft) => {
    if (isScrollingSyncRef.current) {
      return;
    }

    isScrollingSyncRef.current = true;

    if (stickyFooterRef.current) {
      stickyFooterRef.current.scrollLeft = scrollLeft;
    }

    setTimeout(() => {
      isScrollingSyncRef.current = false;
    }, 50);
  };

  const handleStickyFooterScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;

    if (isScrollingSyncRef.current) {
      return;
    }

    isScrollingSyncRef.current = true;

    if (window.mainTableScrollTo) {
      window.mainTableScrollTo(scrollLeft);
    }

    setTimeout(() => {
      isScrollingSyncRef.current = false;
    }, 50);
  };

  const saveScrollPosition = () => {
    if (tabsContainerRef.current) {
      scrollPositionRef.current = tabsContainerRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    const updateStickyFooterVisibility = () => {
      const screenWidth = window.innerWidth;
      const scrolledDown = window.scrollY > 300;

      const isMobileOrTabletDevice = screenWidth < 768;

      if (isMobileOrTabletDevice) {
        setShowStickyFooter(true);
      } else {
        setShowStickyFooter(scrolledDown);
      }
    };

    updateStickyFooterVisibility();
    window.addEventListener('resize', updateStickyFooterVisibility);
    window.addEventListener('scroll', updateStickyFooterVisibility, { passive: true });

    return () => {
      window.removeEventListener('resize', updateStickyFooterVisibility);
      window.removeEventListener('scroll', updateStickyFooterVisibility);
    };
  }, []);

  useEffect(() => {
    if (testId !== selectedTab) {
      scrollPositionRef.current = 0;
    }
    setCurrentPage(1);
    fetchLeaderboard(1);
    fetchOverallTopStudents();
  }, [testId, packId, selectedTab]);

  useEffect(() => {
    if (!loading && currentPage > 0) {
      fetchLeaderboard(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!testDetails || hasTrackedRef.current) return;

    window.webengage.track('Viewed Leaderboard', {
      'User Id': userProfile?.email,
      'Pack Id': packId,
      'Pack Name': selectedPack?.title,
      'Test Id': testId,
      'Test Name': testDetails?.title,
    });

    hasTrackedRef.current = true;
  }, [testDetails]);

  useEffect(() => {
    if (tabsContainerRef.current && scrollPositionRef.current !== undefined) {
      setTimeout(() => {
        if (tabsContainerRef.current) {
          tabsContainerRef.current.scrollLeft = scrollPositionRef.current;
        }
      }, 0);
    }
  });

  const fetchOverallTopStudents = async () => {
    let url = '';
    const effectiveTestId = testId;
    let tabToFetchForThisCall = selectedTab;
    if (isCombinedLocked && selectedTab === 'overall-combined') {
      tabToFetchForThisCall = effectiveTestId;
    }

    if (topStudentsCacheRef.current[tabToFetchForThisCall]) {
      setOverallTopStudents(topStudentsCacheRef.current[tabToFetchForThisCall]);
      return;
    }

    setLoading(true);
    if (tabToFetchForThisCall === 'overall-combined') {
      url = `/api/test/leaderboard/${effectiveTestId}/combined?pack=${packId}&page=1&limit=3`;
    } else {
      url = `/api/test/leaderboard/${tabToFetchForThisCall}?pack=${packId}&page=1&limit=3`;
    }

    try {
      const response = await axios.get(url);
      const { data: innerData, test: apiTest } = response.data;
      const { results } = innerData;
      const safeTestDetails = apiTest || {};
      const apiSubjects = safeTestDetails?.subjects || [];
      const transformedTopStudents = transformLeaderboardData(
        results,
        safeTestDetails,
        currentUserId,
        apiSubjects,
        tabToFetchForThisCall === 'overall-combined',
      );
      topStudentsCacheRef.current[tabToFetchForThisCall] = transformedTopStudents;
      setOverallTopStudents(transformedTopStudents);
    } catch (error) {
      navigate(`/${packId}/${testId}/analysis`);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async (page) => {
    setLoading(true);
    let url = '';
    const effectiveTestId = testId;
    let tabToFetchForThisCall = selectedTab;
    if (isCombinedLocked && selectedTab === 'overall-combined') {
      tabToFetchForThisCall = effectiveTestId;
      setSelectedTab(effectiveTestId);
    }

    if (!tabsInitializedRef.current && searchParams.get('tab') && tabToFetchForThisCall === 'overall-combined') {
      const indivCacheKey = `${effectiveTestId}-${page}`;
      if (!leaderboardCacheRef.current[indivCacheKey]) {
        try {
          const indivRes = await axios.get(`/api/test/leaderboard/${effectiveTestId}?pack=${packId}&page=${page}`);
          const {
            data: indivInner,
            test: indivApiTest,
            userRank: indivUserRank,
            totalPages: indivTotalPages,
            totalResults: indivTotalResults,
          } = indivRes.data;
          const { results: indivResults, isCombinedLocked: indivIsCombinedLocked } = indivInner;
          const indivTestDetails = indivApiTest || {};
          const indivSubjects = indivTestDetails?.subjects || [];
          const indivSingleSubject = indivResults.length > 0 && indivResults[0].subjects.length === 1;
          const indivTransformed = transformLeaderboardData(
            indivResults,
            indivTestDetails,
            currentUserId,
            indivSubjects,
            false,
          );
          const indivUserRankTransformed = indivUserRank
            ? transformLeaderboardData([indivUserRank], indivTestDetails, currentUserId, indivSubjects, false)[0]
            : null;

          leaderboardCacheRef.current[indivCacheKey] = {
            leaderboard: indivTransformed,
            testDetails: indivTestDetails,
            totalPages: indivTotalPages,
            currentUser: indivUserRankTransformed,
            allSubjects: indivSubjects,
            isSingleSubject: indivSingleSubject,
            totalResults: indivTotalResults,
          };

          topStudentsCacheRef.current[effectiveTestId] = indivTransformed.slice(0, 3);

          setLinkedLists(indivInner?.linkedTests);
          setIsCombinedLocked(indivIsCombinedLocked);
          mainTestTitleRef.current = indivTestDetails?.shortTitle || indivTestDetails?.title;
          tabsInitializedRef.current = true;
        } catch (_e) {}
      }
    }

    const cacheKey = `${tabToFetchForThisCall}-${page}`;
    if (leaderboardCacheRef.current[cacheKey]) {
      const cached = leaderboardCacheRef.current[cacheKey];
      settotalResults(cached.totalResults);
      setIsSingleSubject(cached.isSingleSubject);
      setLeaderboard(cached.leaderboard);
      setTestDetails(cached.testDetails);
      setTotalPages(cached.totalPages);
      setCurrentUser(cached.currentUser);
      setAllSubjects(cached.allSubjects);
      setLoading(false);
      return;
    }

    if (tabToFetchForThisCall === 'overall-combined') {
      url = `/api/test/leaderboard/${effectiveTestId}/combined?pack=${packId}&page=${page}`;
    } else {
      url = `/api/test/leaderboard/${tabToFetchForThisCall}?pack=${packId}&page=${page}`;
    }

    try {
      const response = await axios.get(url);
      const { data: innerData, test: apiTest, userRank, totalPages, totalResults } = response.data;
      const { results, isCombinedLocked: apiIsCombinedLocked } = innerData;
      const safeTestDetails = apiTest || {};
      const apiSubjects = safeTestDetails?.subjects || [];
      settotalResults(totalResults);

      if (!tabsInitializedRef.current) {
        setLinkedLists(innerData?.linkedTests);
        setIsCombinedLocked(apiIsCombinedLocked);
        if (tabToFetchForThisCall === testId) {
          mainTestTitleRef.current = safeTestDetails?.shortTitle || safeTestDetails?.title;
        } else if (tabToFetchForThisCall === 'overall-combined' && innerData?.linkedTests?.length > 0) {
          const mainTestFromLinked = innerData.linkedTests.find((t) => t._id === testId);
          mainTestTitleRef.current =
            mainTestFromLinked?.shortTitle || mainTestFromLinked?.title || safeTestDetails?.title;
        } else {
          mainTestTitleRef.current = safeTestDetails?.shortTitle || safeTestDetails?.title;
        }
        tabsInitializedRef.current = true;
      }

      const singleSubjectTest = results.length > 0 && results[0].subjects.length === 1;
      setIsSingleSubject(singleSubjectTest);
      setIsSingleSectionMultipleSubjects(apiTest.singleSectionMultipleSubjects);
      const transformedData = transformLeaderboardData(
        results,
        safeTestDetails,
        currentUserId,
        apiSubjects,
        tabToFetchForThisCall === 'overall-combined',
      );
      const transformedUserRank = userRank
        ? transformLeaderboardData(
            [userRank],
            safeTestDetails,
            currentUserId,
            apiSubjects,
            tabToFetchForThisCall === 'overall-combined',
          )[0]
        : null;

      leaderboardCacheRef.current[cacheKey] = {
        leaderboard: transformedData,
        testDetails: safeTestDetails,
        totalPages,
        currentUser: transformedUserRank,
        allSubjects: apiSubjects,
        isSingleSubject: singleSubjectTest,
        totalResults,
      };

      setLeaderboard(transformedData);
      setTestDetails(safeTestDetails);
      setTotalPages(totalPages);
      setCurrentUser(transformedUserRank);
      setAllSubjects(apiSubjects);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        alert('The leaderboard is being calculated. Please check again in a few minutes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = (student, fromStickyFooter = false) => {
    setSelectedStudent(student);
    setIsStickyFooterCompare(fromStickyFooter);
    setIsModalOpen(true);
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const allTabs = (() => {
    const tabs = [];
    tabs.push({
      id: 'overall-combined',
      title: 'Overall',
      icon: (
        <img
          src="https://cdn.quizrr.in/web-assets/analysis-v3/subjects/overall_2.svg"
          alt="All Subjects"
          className="h-5 w-5"
        />
      ),
      disabled: isCombinedLocked,
      tooltip: isCombinedLocked ? 'You need to attempt\nboth papers to see\nthe combined leaderboard.' : '',
    });

    tabs.push({
      id: testId,
      title: formatTestTitle(mainTestTitleRef.current || testDetails?.title),
      shortTitle: mainTestTitleRef.current || testDetails?.title,
      icon: <img src="https://cdn.quizrr.in/web-assets/pack_ui/full_tests.svg" alt="Test" className="h-5 w-5" />,
      disabled: false,
      tooltip: '',
    });

    if (linkedTests && linkedTests.length > 0) {
      linkedTests.forEach((linkedTest) => {
        tabs.push({
          id: linkedTest._id,
          title: formatTestTitle(linkedTest.title),
          shortTitle: linkedTest.shortTitle || linkedTest.title,
          icon: <img src="https://cdn.quizrr.in/web-assets/pack_ui/full_tests.svg" alt="Test" className="h-5 w-5" />,
          disabled: isCombinedLocked,
          tooltip: isCombinedLocked
            ? `You need to attempt\n${formatTestTitle(linkedTest.title)} to see\nthe leaderboard.`
            : '',
        });
      });
    }
    return tabs;
  })();

  const showTabs = allTabs.length > 2;
  const topperStudent = overallTopStudents.length > 0 ? overallTopStudents[0] : null;

  const subjectKeys =
    !isSingleSubject && allSubjects && allSubjects.length > 0
      ? allSubjects.map((subject) => ({
          key: subject.subjectId.shortName
            ? subject.subjectId.shortName.toLowerCase()
            : subject.subjectId.title.toLowerCase(),
          display: subject.subjectId.shortName || subject.subjectId.title,
        }))
      : [];

  useEffect(() => {
    if (tabsContainerRef.current && scrollPositionRef.current !== undefined) {
      tabsContainerRef.current.scrollLeft = scrollPositionRef.current;
    }
  });

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="min-h-screen max-w-[1200px] flex flex-col mx-auto overflow-x-auto">
        <div className="py-4 md:py-8 px-4 md:px-4 lg:px-0 mb-35 md:mb-15 flex flex-col">
          <div className="flex flex-col items-start gap-3 lg:gap-5 mb-0 lg:mb-6">
            <Button
              onClick={() => navigate(-1)}
              size="icon"
              className="text-[var(--q3-neutral-light)] mb-2 bg-[var(--q3-surface-dimmer)] hover:bg-[var(--q3-surface-dimmer)]/110 rounded-full"
            >
              <ArrowLeft size={24} />
            </Button>
            <div>
              <div className="text-lg md:text-2xl font-semibold text-[var(--q3-neutral-default)]">
                {testDetails?.title}
              </div>
              {/* <Breadcrumb className="mb-6">
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/v2/home">Dashboard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href={`/${packId}/${testId}`}>Test</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>Leaderboard</BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb> */}
            </div>
          </div>

          {showTabs && (
            <div className="mb-8 overflow-x-auto" ref={tabsContainerRef}>
              <TooltipProvider>
                <TabsContainer color="var(--q3-neutral-default)">
                  {allTabs.map((tab) =>
                    tab.disabled && tab.tooltip ? (
                      <Tooltip key={tab.id}>
                        <TooltipTrigger asChild>
                          <Tab
                            isActive={selectedTab === tab.id}
                            onClick={() => {
                              if (!tab.disabled) {
                                saveScrollPosition();
                                setSelectedTab(tab.id);
                                setCurrentPage(1);
                              }
                            }}
                            disabled={tab.disabled}
                          >
                            {tab.icon && tab.icon}
                            <p className="mb-0 text-base flex items-center gap-1 text-[var(--q3-neutral-light)]">
                              {tab.shortTitle || tab.title} {tab.disabled && <Lock className="h-4 w-4" />}
                            </p>
                          </Tab>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tab
                        key={tab.id}
                        isActive={selectedTab === tab.id}
                        onClick={() => {
                          if (!tab.disabled) {
                            saveScrollPosition();
                            setSelectedTab(tab.id);
                            setCurrentPage(1);
                          }
                        }}
                        disabled={tab.disabled}
                        title={tab.tooltip}
                      >
                        {tab.icon && tab.icon}
                        <p className="mb-0 flex text-base items-center gap-1 text-[var(--q3-neutral-light)]">
                          {tab.shortTitle || tab.title} {tab.disabled && <Lock className="h-4 w-4" />}
                        </p>
                      </Tab>
                    ),
                  )}
                </TabsContainer>
              </TooltipProvider>
            </div>
          )}

          {loading ? (
            <LeaderboardSkeleton />
          ) : (
            <>
              <div className="hidden md:flex md:justify-center lg:justify-evenly flex-wrap mb-8 md:gap-10 lg:gap-3">
                {overallTopStudents.map((student) => (
                  <StudentCard
                    topperStudent={topperStudent?.raw}
                    onCompare={(student) => handleCompare(student, false)}
                    key={student.rank}
                    student={student}
                    isSingleSubject={isSingleSubject}
                    isSingleSectionMultipleSubjects={isSingleSectionMultipleSubjects}
                    allSubjects={allSubjects}
                  />
                ))}

                {currentUser &&
                  currentUser.rank > 3 &&
                  !overallTopStudents.some((s) => s.rank === currentUser.rank) && (
                    <StudentCard
                      topperStudent={topperStudent?.raw}
                      onCompare={(student) => handleCompare(student, false)}
                      student={currentUser}
                      isSingleSubject={isSingleSubject}
                      isSingleSectionMultipleSubjects={isSingleSectionMultipleSubjects}
                      allSubjects={allSubjects}
                    />
                  )}
              </div>

              <LeaderboardTable
                data={leaderboard}
                onCompare={(student) => handleCompare(student, false)}
                isSingleSubject={isSingleSubject}
                allSubjects={allSubjects}
                onTableScroll={handleMainTableScroll}
                isSingleSectionMultipleSubjects={isSingleSectionMultipleSubjects}
                topperStudent={topperStudent?.raw}
              />

              {totalPages > 1 && (
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    {(paginationRange || []).map((pageNumber, index) => {
                      if (typeof pageNumber === 'string') {
                        return (
                          <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            isActive={pageNumber === currentPage}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(pageNumber);
                            }}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>

        {currentUser && showStickyFooter && (
          <div
            className="fixed bottom-[64px] md:bottom-0 z-20 py-2 px-4 lg:px-2 border-1 rounded-none md:rounded-tr-2xl md:rounded-tl-2xl q3-glass"
            style={{
              left: isMobileOrTablet ? '50%' : 'calc(100px + (100vw - 100px - min(1200px, 100vw - 100px)) / 2)',
              transform: isMobileOrTablet ? 'translateX(-50%)' : 'none',
              width: isMobileOrTablet ? '100%' : 'min(1200px, calc(100vw - 100px))',
              maxWidth: '1200px',
            }}
          >
            <div
              className="overflow-x-auto"
              ref={stickyFooterRef}
              onScroll={handleStickyFooterScroll}
              style={{ width: '100%' }}
            >
              {(() => {
                const rankWidth = isMobileOrTablet ? 60 : 100;
                const nameWidth = isMobileOrTablet ? 140 : 280;
                const scoreWidth = isMobileOrTablet ? 140 : 180;
                const subjectWidth = subjectKeys.length * (isMobileOrTablet ? 60 : 120);
                const accuracyWidth = isMobileOrTablet ? 60 : 100;
                const compareWidth = isMobileOrTablet ? 140 : 160;
                const minTableWidth = rankWidth + nameWidth + scoreWidth + subjectWidth + accuracyWidth + compareWidth;

                return (
                  <div style={{ minWidth: `${minTableWidth}px`, width: '100%' }}>
                    <Table
                      className="w-full"
                      style={{
                        tableLayout: 'fixed',
                        width: `max(100%, ${minTableWidth}px)`,
                      }}
                    >
                      <TableBody>
                        <TableRow style={{ backgroundColor: 'var(--q3-blue-lightest)' }}>
                          <TableCell
                            className=" font-medium"
                            style={{
                              width: `${rankWidth}px`,
                              maxWidth: `${rankWidth}px`,
                              minWidth: `${rankWidth}px`,
                            }}
                          >
                            <Badge className="rounded-full bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-default)] border-[var(--q3-stroke-normal)] w-10 h-10">
                              {currentUser.rank}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className=""
                            style={{
                              width: `${nameWidth}px`,
                              maxWidth: `${nameWidth}px`,
                              minWidth: `${nameWidth}px`,
                            }}
                          >
                            <div className="flex items-center gap-2 overflow-hidden">
                              <Avatar className="h-8 w-8 hidden md:block flex-shrink-0">
                                <AvatarImage src={currentUser.avatar || '/placeholder.svg'} />
                                <AvatarFallback>
                                  {currentUser.name
                                    ?.split(' ')
                                    .map((n) => n[0])
                                    .join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="overflow-hidden">
                                <span className="font-medium inline md:hidden  truncate">
                                  {currentUser.name.length > 16
                                    ? currentUser.name.substring(0, 10) + '...'
                                    : currentUser.name}{' '}
                                  (You)
                                </span>
                                <span className="font-medium hidden md:inline  truncate">{currentUser.name} (You)</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            className="text-center"
                            style={{
                              width: `${scoreWidth}px`,
                              maxWidth: `${scoreWidth}px`,
                              minWidth: `${scoreWidth}px`,
                            }}
                          >
                            <div
                              className="inline-flex items-center justify-center border-1 rounded-full bg-[var(--q3-surface-dim)] px-3 py-1"
                              style={{ color: 'var(--q3-neutral-default)' }}
                            >
                              <span className="font-semibold text-base text-[var(--q3-neutral-default)]">
                                {currentUser?.overallScore}
                              </span>
                              <span className="text-xs text-[var(--q3-neutral-light)]">/{currentUser.maxScore}</span>
                            </div>
                          </TableCell>
                          {!isSingleSubject &&
                            !isSingleSectionMultipleSubjects &&
                            allSubjects.map((subject) => (
                              <TableCell
                                key={subject._id}
                                className=" text-center font-medium"
                                style={{
                                  width: `${isMobileOrTablet ? 60 : 120}px`,
                                  maxWidth: `${isMobileOrTablet ? 60 : 120}px`,
                                  minWidth: `${isMobileOrTablet ? 60 : 120}px`,
                                }}
                              >
                                {currentUser[
                                  subject.subjectId.shortName
                                    ? subject.subjectId.shortName.toLowerCase()
                                    : subject.subjectId.title.toLowerCase()
                                ] ?? '--'}
                              </TableCell>
                            ))}
                          <TableCell
                            className="text-center font-medium"
                            style={{
                              width: `${accuracyWidth}px`,
                              maxWidth: `${accuracyWidth}px`,
                              minWidth: `${accuracyWidth}px`,
                            }}
                          >
                            {currentUser.accuracy}%
                          </TableCell>
                          <TableCell
                            className="text-center"
                            style={{
                              width: `${compareWidth}px`,
                              maxWidth: `${compareWidth}px`,
                              minWidth: `${compareWidth}px`,
                            }}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[var(--q3-accent-normal)] hover:text-[var(--q3-accent-normal)]/90 bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dim)] hover:scale-[1.02] rounded-full border-1"
                              onClick={() => handleCompare(currentUser, true)}
                            >
                              Compare
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <PerformanceModal
          isOpen={isModalOpen}
          isCombined={selectedTab === 'overall-combined'}
          onClose={() => setIsModalOpen(false)}
          selectedStudent={selectedStudent?.raw}
          currentUser={currentUser?.raw}
          isSingleSubject={isSingleSubject}
          isSingleSectionMultipleSubjects={isSingleSectionMultipleSubjects}
          testDetails={testDetails}
          isStickyFooterCompare={isStickyFooterCompare}
          topperStudent={topperStudent?.raw}
          totalResults={totalResults}
          allSubjects={allSubjects}
        />
      </div>
    </AppLayout>
  );
}
